import { useState, useEffect } from 'react';
import { useApi } from '@/api/axios';

export function useCollectDetail(nsr: string | undefined, agreementUuid: string | undefined) {
  const api = useApi();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [collect, setCollect] = useState<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [employer, setEmployer] = useState<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [employee, setEmployee] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  useEffect(() => {
    if (!nsr || !agreementUuid) return;
    let cancelled = false;
    const maxRetries = 3;
    const baseDelay = 500;

    const fetchAll = async (attempt = 1) => {
      if (attempt === 1) {
        setLoading(true);
        setError(null);
      }
      console.debug(`[useCollectDetail] nsr:`, nsr, 'agreementUuid:', agreementUuid, `Attempt: ${attempt}`);
      try {
        const collectsRes = await api.get(`/agreements/${agreementUuid}/collects/punchcard`, { params: { nsr } });
        console.debug('[useCollectDetail] collectsRes:', collectsRes.data);
        const collectItem = Array.isArray(collectsRes.data) ? collectsRes.data[0] : null;
        if (!collectItem) throw new Error('Registro de ponto não encontrado');

        const agreementRes = await api.get(`/agreements/${agreementUuid}`);
        console.debug('[useCollectDetail] agreementRes:', agreementRes.data);
        let contractorData = null;
        if (agreementRes.data.contractor) {
          try {
            const contractorRes = await api.get(`/agreements/${agreementUuid}/contractor/${agreementRes.data.contractor}`);
            console.debug('[useCollectDetail] contractorRes:', contractorRes.data);
            contractorData = contractorRes.data;
          } catch (e) {
            console.debug('[useCollectDetail] contractor fetch error:', e);
          }
        }

        let workerUid = agreementRes.data.worker;
        if (!workerUid && collectItem.collector?.uuid) {
          workerUid = collectItem.collector.uuid;
        }
        let employeeData = null;
        if (workerUid) {
          try {
            const employeeRes = await api.get(`/agreements/${agreementUuid}/worker/${workerUid}`);
            console.debug('[useCollectDetail] employeeRes:', employeeRes.data);
            employeeData = employeeRes.data;
          } catch (e) {
            console.debug('[useCollectDetail] employee fetch error:', e);
          }
        } else {
          console.debug('[useCollectDetail] No workerUid found');
        }

        if (!cancelled) {
          setCollect(collectItem);
          setEmployer(contractorData || agreementRes.data);
          setEmployee(employeeData);
          setLoading(false);
        }
      } catch (err: unknown) {
        console.error(`[useCollectDetail] error (attempt ${attempt}):`, err);
        if (attempt < maxRetries) {
          setTimeout(() => {
            if (!cancelled) fetchAll(attempt + 1);
          }, baseDelay * Math.pow(2, attempt - 1));
        } else {
          if (!cancelled) {
            setError(err instanceof Error ? err.message : 'Erro ao buscar registro de ponto');
            setLoading(false);
          }
        }
      }
    };

    fetchAll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nsr, agreementUuid]);

  const handleDownloadPDF = async () => {
    if (!agreementUuid || !nsr) throw new Error('Dados insuficientes para gerar comprovante');
    setPdfLoading(true);
    setPdfError(null);
    try {
      const res = await api.post(
        `/agreements/${agreementUuid}/nsr/${nsr}/generate/ticket`,
        {},
        { responseType: 'blob' },
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `comprovante-ponto-nsr-${nsr}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: unknown) {
      setPdfError(err instanceof Error ? err.message : 'Erro ao gerar comprovante PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  return { collect, employer, employee, loading, error, pdfLoading, pdfError, handleDownloadPDF };
}
